/**
 * Promotions — Manage promotional overrides for upgrade matrix rules.
 *
 * Each promotion temporarily replaces the transaction fee, proration rule,
 * and first-month rule for a specific from→to tier transition during a date range.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { Promotion, TierRecord } from '@/types';
import { getPromotions, savePromotion, deletePromotion, getTiers } from '@/services/adminApi';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isActive(promo: Promotion): boolean {
  const today = new Date().toISOString().split('T')[0];
  return promo.startDate <= today && promo.endDate >= today;
}

function isUpcoming(promo: Promotion): boolean {
  const today = new Date().toISOString().split('T')[0];
  return promo.startDate > today;
}

function isExpired(promo: Promotion): boolean {
  const today = new Date().toISOString().split('T')[0];
  return promo.endDate < today;
}

function statusChip(promo: Promotion) {
  if (isActive(promo)) return <Chip label="Active" color="success" size="small" />;
  if (isUpcoming(promo)) return <Chip label="Upcoming" color="info" size="small" />;
  return <Chip label="Expired" size="small" sx={{ opacity: 0.5 }} />;
}

function emptyPromotion(): Promotion {
  const today = new Date().toISOString().split('T')[0];
  const nextMonth = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  return {
    promoId: '',
    description: '',
    fromTierId: '',
    fromType: 'basic',
    fromRegion: 'DMV',
    toTierId: '',
    toType: 'premier',
    toRegion: 'DMV',
    transactionFeeOverride: '0',
    prorationRuleOverride: '0',
    firstMonthRuleOverride: '0',
    startDate: today,
    endDate: nextMonth,
  };
}

// ---------------------------------------------------------------------------
// Edit Dialog
// ---------------------------------------------------------------------------

interface EditDialogProps {
  open: boolean;
  promo: Promotion;
  isNew: boolean;
  saving: boolean;
  tiers: TierRecord[];
  regions: string[];
  onChange: (p: Promotion) => void;
  onSave: () => void;
  onCancel: () => void;
}

const VALID_VALUE_RE = /^(X|0|\d+(\.\d{1,2})?|%\d{1,3})$/;

function EditPromotionDialog({ open, promo, isNew, saving, tiers, regions, onChange, onSave, onCancel }: EditDialogProps) {
  const update = <K extends keyof Promotion>(key: K, value: Promotion[K]) => {
    onChange({ ...promo, [key]: value });
  };

  const fromTiers = tiers.filter(t => t.region === promo.fromRegion);
  const toTiers = tiers.filter(t => t.region === promo.toRegion);

  const feeValid = VALID_VALUE_RE.test(promo.transactionFeeOverride.trim());
  const proValid = VALID_VALUE_RE.test(promo.prorationRuleOverride.trim());
  const fmValid = VALID_VALUE_RE.test(promo.firstMonthRuleOverride.trim());
  const allValid = feeValid && proValid && fmValid && promo.promoId && promo.description && promo.fromTierId && promo.toTierId;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{isNew ? 'Create Promotion' : `Edit: ${promo.description}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {/* Identity */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Promotion ID"
              value={promo.promoId}
              onChange={(e) => update('promoId', e.target.value.replace(/\s/g, '-').toLowerCase())}
              fullWidth
              size="small"
              disabled={!isNew}
              helperText="e.g., spring-2026-georgia"
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              label="Description"
              value={promo.description}
              onChange={(e) => update('description', e.target.value)}
              fullWidth
              size="small"
              helperText="e.g., Join Premier for $1 — Spring 2026"
            />
          </Grid>
        </Grid>

        {/* Date range */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Start Date"
              type="date"
              value={promo.startDate}
              onChange={(e) => update('startDate', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="End Date"
              type="date"
              value={promo.endDate}
              onChange={(e) => update('endDate', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* From tier */}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>From (source tier)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Region</InputLabel>
              <Select value={promo.fromRegion} label="Region" onChange={(e) => update('fromRegion', e.target.value)}>
                {regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier</InputLabel>
              <Select value={promo.fromTierId} label="Tier" onChange={(e) => update('fromTierId', e.target.value)}>
                {fromTiers.map(t => <MenuItem key={t.tierId} value={t.tierId}>{t.displayName}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={promo.fromType} label="Type" onChange={(e) => update('fromType', e.target.value)}>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="premier">Premier</MenuItem>
                <MenuItem value="boxing">Boxing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* To tier */}
        <Typography variant="subtitle2">To (target tier)</Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Region</InputLabel>
              <Select value={promo.toRegion} label="Region" onChange={(e) => update('toRegion', e.target.value)}>
                {regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Tier</InputLabel>
              <Select value={promo.toTierId} label="Tier" onChange={(e) => update('toTierId', e.target.value)}>
                {toTiers.map(t => <MenuItem key={t.tierId} value={t.tierId}>{t.displayName}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select value={promo.toType} label="Type" onChange={(e) => update('toType', e.target.value)}>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="premier">Premier</MenuItem>
                <MenuItem value="boxing">Boxing</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Override values */}
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Override Values</Typography>
        <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            These values replace the normal upgrade matrix cell for this from→to transition during the promotion period.
            Use X=default, 0=free, dollar amount, or %nn=discount.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Transaction Fee Override"
              value={promo.transactionFeeOverride}
              onChange={(e) => update('transactionFeeOverride', e.target.value)}
              fullWidth
              size="small"
              error={!feeValid}
              helperText="X, 0, $amount, %nn"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Proration Override"
              value={promo.prorationRuleOverride}
              onChange={(e) => update('prorationRuleOverride', e.target.value)}
              fullWidth
              size="small"
              error={!proValid}
              helperText="X, 0, $amount, %nn"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="First Month Override"
              value={promo.firstMonthRuleOverride}
              onChange={(e) => update('firstMonthRuleOverride', e.target.value)}
              fullWidth
              size="small"
              error={!fmValid}
              helperText="X, 0, $amount, %nn"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={onSave} variant="contained" disabled={saving || !allValid}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function Promotions() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [tiers, setTiers] = useState<TierRecord[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPromo, setEditPromo] = useState<Promotion>(emptyPromotion());
  const [editIsNew, setEditIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingPromo, setDeletingPromo] = useState<Promotion | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [promos, allTiers] = await Promise.all([getPromotions(), getTiers()]);
      setPromotions(promos);
      setTiers(allTiers);
      const r = new Set(allTiers.map(t => t.region));
      setRegions(Array.from(r).sort());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Sort: active first, then upcoming, then expired
  const sorted = [...promotions].sort((a, b) => {
    const order = (p: Promotion) => isActive(p) ? 0 : isUpcoming(p) ? 1 : 2;
    const diff = order(a) - order(b);
    if (diff !== 0) return diff;
    return a.startDate.localeCompare(b.startDate);
  });

  const handleAdd = () => {
    setEditPromo(emptyPromotion());
    setEditIsNew(true);
    setDialogOpen(true);
  };

  const handleEdit = (promo: Promotion) => {
    setEditPromo({ ...promo });
    setEditIsNew(false);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await savePromotion(editPromo);
      setDialogOpen(false);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPromo) return;
    try {
      await deletePromotion(deletingPromo.promoId);
      setDeleteConfirmOpen(false);
      setDeletingPromo(null);
      void loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete promotion');
    }
  };

  const tierName = (region: string, tierId: string): string => {
    const t = tiers.find(t => t.region === region && t.tierId === tierId);
    return t ? t.displayName : tierId;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Promotions</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip label={`${promotions.length} promotions`} color="primary" variant="outlined" />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
            Add Promotion
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : promotions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No promotions configured. Create one to override upgrade matrix rules for a date range.</Typography>
        </Paper>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>From</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>To</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Overrides</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Dates</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((promo) => (
                <TableRow
                  key={promo.promoId}
                  hover
                  sx={{
                    opacity: isExpired(promo) ? 0.5 : 1,
                    backgroundColor: isActive(promo) ? '#e8f5e9' : undefined,
                  }}
                >
                  <TableCell>{statusChip(promo)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {promo.promoId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{promo.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${promo.fromRegion} / ${promo.fromTierId} / ${promo.fromType}`} arrow>
                      <Typography variant="caption">
                        {tierName(promo.fromRegion, promo.fromTierId)} {promo.fromType}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`${promo.toRegion} / ${promo.toTierId} / ${promo.toType}`} arrow>
                      <Typography variant="caption">
                        {tierName(promo.toRegion, promo.toTierId)} {promo.toType}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${promo.transactionFeeOverride}/${promo.prorationRuleOverride}/${promo.firstMonthRuleOverride}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {promo.startDate} — {promo.endDate}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(promo)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => { setDeletingPromo(promo); setDeleteConfirmOpen(true); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Edit/Add Dialog */}
      <EditPromotionDialog
        open={dialogOpen}
        promo={editPromo}
        isNew={editIsNew}
        saving={saving}
        tiers={tiers}
        regions={regions}
        onChange={setEditPromo}
        onSave={handleSave}
        onCancel={() => setDialogOpen(false)}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Promotion</DialogTitle>
        <DialogContent>
          <Typography>
            Delete promotion <strong>{deletingPromo?.description}</strong> ({deletingPromo?.promoId})?
            The normal upgrade matrix rules will apply once this promotion is removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
